/**
 * Locale switcher — Punjabi ⇄ English.
 *
 * Default is Punjabi (per the field-rep audience). Any explicit choice the
 * surveyor makes via the header toggle persists across launches in
 * @capacitor/preferences (web → localStorage, native → SharedPreferences).
 *
 * Singleton state — every component that calls `useLocale()` shares the same
 * ref, so the header toggle re-renders every label in the app.
 */
import { onMounted, ref, watch } from 'vue';
import { Preferences } from '@capacitor/preferences';
import { i18n } from '@/i18n';

export type Locale = 'pa' | 'en';

const LOCALE_KEY = 'wattr.locale.v1';

const locale = ref<Locale>('pa');
let booted = false;

function applyToI18n(l: Locale) {
  i18n.global.locale.value = l;
  // Reflect in the document for any platform that styles by lang attribute
  // (Android system fonts pick a different default for Gurmukhi vs Latin).
  document.documentElement.setAttribute('lang', l);
}

async function load() {
  try {
    const { value } = await Preferences.get({ key: LOCALE_KEY });
    if (value === 'pa' || value === 'en') {
      locale.value = value;
    }
  } catch {
    /* ignore — fall back to the default 'pa' */
  }
  applyToI18n(locale.value);
}

async function persist() {
  try {
    await Preferences.set({ key: LOCALE_KEY, value: locale.value });
  } catch {
    /* ignore */
  }
}

export function useLocale() {
  onMounted(async () => {
    if (booted) return;
    booted = true;
    await load();
  });

  watch(locale, (next) => {
    applyToI18n(next);
    void persist();
  });

  function setLocale(l: Locale) {
    locale.value = l;
  }

  function toggle() {
    locale.value = locale.value === 'pa' ? 'en' : 'pa';
  }

  return { locale, setLocale, toggle };
}
